import { Box, Typography, IconButton, Collapse, Divider, Link } from '@mui/material';
import React, { useState, useMemo, useEffect } from 'react';
import { PureLightThemeCaaS } from 'src/theme/schemes/PureLightTheme';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { faqData, flattenFaqForSeo } from 'src/utils/helpMenuContent';
import { Browser } from '@capacitor/browser';
import { isPlatform } from '@ionic/react';
import TextFieldHelp from '../TextField/TextFieldHelp';
import { subscribeFrontRuntimeConfig } from 'src/services/runtimeFrontConfig';
import { PROD_FRONTEND_URL, WEB_FRONTEND_LINK_PATTERNS } from 'src/config/frontendUrls';
import { Helmet } from 'react-helmet-async';
import {
  SEO_FAQ_DESCRIPTION,
  SEO_FAQ_TITLE,
  SEO_OG_IMAGE,
  SEO_SITE_NAME,
  SEO_TWITTER_HANDLE,
  buildFaqCanonicalUrl,
} from 'src/config/seo.config';

interface FaqProps {
    handleBack: () => void;
    handleClose: () => void;
    standalone?: boolean;
}

// Helper function to handle store redirects with explicit store type
const handleStoreClick = async (e: React.MouseEvent, storeType: 'appstore' | 'playstore') => {
    e.preventDefault();
    e.stopPropagation();

    // URLs for each store
    // const appStoreUrl = "https://apps.apple.com/search?q=RotoGPT"; 
    // const playStoreUrl = "https://play.google.com/store/search?q=RotoGPT&c=apps";

    // REAL ONES:
    const appStoreUrl = "https://apps.apple.com/us/app/rotogpt/id6736617092";
    const playStoreUrl = "https://play.google.com/store/apps/details?id=com.rotogpt.chat";


    // Use the explicitly specified store type
    const url = storeType === 'appstore' ? appStoreUrl : playStoreUrl;

    try {
        if (isPlatform("hybrid")) {
            // Use Capacitor Browser for native platforms
            await Browser.open({ url });
        } else {
            // Fallback for web platform
            window.open(url, "_blank");
        }
    } catch (error) {
        console.error("Error opening store:", error);
        // Additional fallback: try to open in new tab
        try {
            window.open(url, "_blank");
        } catch (fallbackError) {
            console.error("Fallback also failed:", fallbackError);
        }
    }
};

// Helper function to handle web link
const handleWebClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(PROD_FRONTEND_URL, '_blank');
};

// Helper function to render text with clickable links
const renderTextWithLinks = (text: string) => {
    // Patterns to match: App Store, Google Play, iOS, Android, RotoGPT.com, chat.rotogpt.com (legacy)
    const patterns = [
        { pattern: /App Store/g, handler: (e: React.MouseEvent) => handleStoreClick(e, 'appstore') },
        { pattern: /Google Play/g, handler: (e: React.MouseEvent) => handleStoreClick(e, 'playstore') },
        // { pattern: /iOS/g, handler: (e: React.MouseEvent) => handleStoreClick(e, 'appstore') },
        // { pattern: /Android/g, handler: (e: React.MouseEvent) => handleStoreClick(e, 'playstore') },
        // { pattern: /RotoGPT\.com/g, handler: handleWebClick },
        ...WEB_FRONTEND_LINK_PATTERNS.map(({ pattern }) => ({ pattern, handler: handleWebClick })),
    ];

    let result: React.ReactNode[] = [];
    let lastIndex = 0;
    let keyCounter = 0;

    // Find all matches
    const matches: Array<{ start: number; end: number; pattern: typeof patterns[0] }> = [];
    patterns.forEach(({ pattern, handler }) => {
        let match;
        while ((match = pattern.exec(text)) !== null) {
            matches.push({
                start: match.index,
                end: match.index + match[0].length,
                pattern: { pattern, handler },
            });
        }
    });

    // Sort matches by position
    matches.sort((a, b) => a.start - b.start);

    // Build result with links
    matches.forEach((match) => {
        // Add text before match
        if (match.start > lastIndex) {
            result.push(text.substring(lastIndex, match.start));
        }

        // Add link
        const linkText = text.substring(match.start, match.end);
        result.push(
            <Link
                key={keyCounter++}
                component="span"
                onClick={match.pattern.handler}
                sx={{
                    color: 'blue',
                    cursor: 'pointer',
                    display: 'inline',
                    textDecoration: 'none',
                    verticalAlign: 'baseline',
                    lineHeight: 'inherit',
                    '&:hover': {
                        textDecoration: 'underline',
                    },
                }}
            >
                {linkText}
            </Link>
        );

        lastIndex = match.end;
    });

    // Add remaining text
    if (lastIndex < text.length) {
        result.push(text.substring(lastIndex));
    }

    return result.length > 0 ? result : text;
};

// Escape special regex chars for use in RegExp
const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Highlight search term in plain text (category title, question text)
const highlightSearch = (text: string, search: string): React.ReactNode => {
    if (!search.trim()) return text;
    const lower = search.toLowerCase().trim();
    const parts = text.split(new RegExp(`(${escapeRegex(search)})`, 'gi'));
    return parts.map((part, i) =>
        part.toLowerCase() === lower ? (
            <Box
                key={i}
                component="span"
                sx={{ bgcolor: "#f99067", borderRadius: 0 }}
            >
                {part}
            </Box>
        ) : (
            <React.Fragment key={i}>{part}</React.Fragment>
        )
    );
};

// Helper function to render answer text with bullet points and links
const renderAnswer = (answer: string) => {
    const lines = answer.split('\n');
    return lines.map((line, index) => {
        // Check if line starts with bullet point
        if (line.trim().startsWith('•')) {
            return (
                <Box
                    key={index}
                    component="span"
                    sx={{
                        display: 'block',
                        // pl: 2,
                        p: 0.2,
                    }}
                >
                    {renderTextWithLinks(line)}
                </Box>
            );
        }
        // Regular line with links
        return (
            <React.Fragment key={index}>
                {renderTextWithLinks(line)}
                {index < lines.length - 1}
            </React.Fragment>
        );
    });
};

// Answer with search term highlighted (keeps links and bullet structure)
const renderAnswerWithHighlight = (answer: string, search: string): React.ReactNode => {
    if (!search.trim()) return renderAnswer(answer);
    const lower = search.toLowerCase().trim();
    const lines = answer.split('\n');
    return lines.map((line, lineIdx) => {
        const parts = line.split(new RegExp(`(${escapeRegex(search)})`, 'gi'));
        const content = parts.map((part, i) =>
            part.toLowerCase() === lower ? (
                <Box key={i} component="span" sx={{ bgcolor: "#f99067", borderRadius: 0 }}>{part}</Box>
            ) : (
                <React.Fragment key={i}>{renderTextWithLinks(part)}</React.Fragment>
            )
        );
        if (line.trim().startsWith('•')) {
            return <Box key={lineIdx} component="span" sx={{ display: 'block', p: 0.2 }}>{content}</Box>;
        }
        return <React.Fragment key={lineIdx}>{content}{lineIdx < lines.length - 1}</React.Fragment>;
    });
};

const categoryExpandedKey = (categoryIndex: number) => String(categoryIndex);

const resolveFaqAnswer = (a: string | (() => string)): string => (typeof a === 'function' ? a() : a);

const Faq = ({ handleBack, handleClose, standalone = false }: FaqProps) => {
    const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
    const [searchText, setSearchText] = useState("");
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [runtimeRefreshToken, setRuntimeRefreshToken] = useState(0);
    useEffect(() => subscribeFrontRuntimeConfig(() => setRuntimeRefreshToken((n) => n + 1)), []);

    const toggleCategory = (categoryIndex: number) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            const key = categoryExpandedKey(categoryIndex);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const isCategoryExpanded = (categoryIndex: number) =>
        expandedCategories.has(categoryExpandedKey(categoryIndex));

    // Filter FAQ by search: category name OR question/answer (q/a) text
    const filteredFaqData = useMemo(() => {
        if (!searchText.trim()) return faqData;
        const lower = searchText.toLowerCase().trim();
        const questionMatches = (q: { q: string; a: string | (() => string) }) =>
            q.q.toLowerCase().includes(lower) || resolveFaqAnswer(q.a).toLowerCase().includes(lower);
        return faqData
            .map((category) => {
                const categoryNameMatches = category.category.toLowerCase().includes(lower);
                if (categoryNameMatches) return category; // show whole category
                return {
                    ...category,
                    subCategories: category.subCategories
                        ?.map((sub) => ({
                            ...sub,
                            questions: sub.questions.filter(questionMatches),
                        }))
                        .filter((sub) => sub.questions.length > 0) ?? [],
                    questions: category.questions?.filter(questionMatches),
                };
            })
            .filter(
                (cat) =>
                    (cat.subCategories?.length ?? 0) > 0 || (cat.questions?.length ?? 0) > 0
            );
    }, [searchText, runtimeRefreshToken]);

    const questionKey = (categoryIndex: number, subCategoryIndex: number | null, questionIndex: number) =>
        subCategoryIndex !== null
            ? `${categoryIndex}-${subCategoryIndex}-${questionIndex}`
            : `${categoryIndex}-${questionIndex}`;

    // Toggle question: when searching allow multiple open; when not searching only one open
    const handleToggleQuestion = (categoryIndex: number, subCategoryIndex: number | null, questionIndex: number) => {
        const key = questionKey(categoryIndex, subCategoryIndex, questionIndex);
        if (searchText.trim()) {
            setExpandedQuestions((prev) => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
            });
        } else {
            setExpandedQuestions((prev) => (prev.has(key) ? new Set() : new Set([key])));
        }
    };

    const isQuestionExpanded = (categoryIndex: number, subCategoryIndex: number | null, questionIndex: number) =>
        expandedQuestions.has(questionKey(categoryIndex, subCategoryIndex, questionIndex));

    // When search is cleared: collapse all. When search has value: expand all result categories and questions
    useEffect(() => {
        if (!searchText.trim()) {
            setExpandedCategories(new Set());
            setExpandedQuestions(new Set());
        } else {
            const catKeys = new Set<string>();
            const qKeys = new Set<string>();
            filteredFaqData.forEach((category, categoryIndex) => {
                catKeys.add(categoryExpandedKey(categoryIndex));
                category.questions?.forEach((_, questionIndex) => {
                    qKeys.add(questionKey(categoryIndex, null, questionIndex));
                });
            });
            setExpandedCategories(catKeys);
            setExpandedQuestions(qKeys);
        }
    }, [searchText, filteredFaqData]);

    const canonicalUrl = buildFaqCanonicalUrl(process.env.REACT_APP_FRONTEND_URI_WEB || PROD_FRONTEND_URL);
    const faqJsonLd = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: flattenFaqForSeo().slice(0, 50).map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
        })),
    });

    return (
        <>
            {standalone ? (
                <Helmet>
                    <title>{SEO_FAQ_TITLE}</title>
                    <meta name="description" content={SEO_FAQ_DESCRIPTION} />
                    <link rel="canonical" href={canonicalUrl} />
                    <meta property="og:type" content="website" />
                    <meta property="og:site_name" content={SEO_SITE_NAME} />
                    <meta property="og:title" content={SEO_FAQ_TITLE} />
                    <meta property="og:description" content={SEO_FAQ_DESCRIPTION} />
                    <meta property="og:url" content={canonicalUrl} />
                    <meta property="og:image" content={SEO_OG_IMAGE} />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:site" content={SEO_TWITTER_HANDLE} />
                    <meta name="twitter:title" content={SEO_FAQ_TITLE} />
                    <meta name="twitter:description" content={SEO_FAQ_DESCRIPTION} />
                    <meta name="twitter:image" content={SEO_OG_IMAGE} />
                    <script type="application/ld+json">{faqJsonLd}</script>
                </Helmet>
            ) : null}
            {/* Content */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    minHeight: 0,
                    p: 3,
                    pt: 1,
                    WebkitOverflowScrolling: "touch",
                    m: "0 auto",
                    bgcolor: "#EDEDED",
                    ...(standalone ? { height: "100%" } : {}),
                }}
            >
                {/* Search - same as Glossary: TextFieldHelp + filter by q/a */}
                <Box sx={{ bgcolor: "#EDEDED", pb: 1 }}>
                    <TextFieldHelp
                        searchText={searchText}
                        setSearchText={setSearchText}
                        label="Search terms"
                    />
                </Box>
                {filteredFaqData.length === 0 ? (
                    <Typography
                        variant="body2"
                        color={PureLightThemeCaaS.colors.alpha.black[100]}
                        sx={{ py: 2 }}
                    >
                        No terms match your search.
                    </Typography>
                ) : (
                    filteredFaqData.map((category, categoryIndex) => {
                        const categoryOpen = isCategoryExpanded(categoryIndex);
                        return (
                            <Box key={categoryIndex} sx={{ width: "100%", mb: 0 }}>
                                {/* Category Title - expandable row with +/- */}
                                <Box
                                    onClick={() => toggleCategory(categoryIndex)}
                                    sx={{
                                        cursor: "pointer",
                                        py: 0.5,
                                        width: "100%",
                                        bgcolor: "#EDEDED",
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        // mt: categoryIndex > 0 ? 1 : 0,
                                    }}
                                >
                                    <Typography
                                        variant="h5"
                                        color={PureLightThemeCaaS.colors.alpha.black[100]}
                                        sx={{
                                            fontSize: "20px",
                                            fontWeight: 600,
                                            flex: 1,
                                            textAlign: "left",
                                            pr: 2,
                                        }}
                                    >
                                        {searchText.trim()
                                            ? highlightSearch(category.category, searchText.trim())
                                            : category.category}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        sx={{
                                            color: PureLightThemeCaaS.colors.alpha.black[100],
                                            flexShrink: 0,
                                            position: "relative",
                                            width: 40,
                                            height: 40,
                                            "&:hover": { bgcolor: "transparent" },
                                            "&:active": { bgcolor: "transparent" },
                                            "&.Mui-focusVisible": { bgcolor: "transparent" },
                                        }}
                                    >
                                        <AddIcon
                                            sx={{
                                                fontSize: "20px",
                                                position: "absolute",
                                                opacity: categoryOpen ? 0 : 1,
                                                transform: categoryOpen ? "rotate(45deg)" : "rotate(0deg)",
                                                transition: "opacity 0.25s ease-in-out, transform 0.3s ease-in-out",
                                            }}
                                        />
                                        <RemoveIcon
                                            sx={{
                                                fontSize: "20px",
                                                position: "absolute",
                                                opacity: categoryOpen ? 1 : 0,
                                                transition: "opacity 0.25s ease-in-out",
                                            }}
                                        />
                                    </IconButton>
                                </Box>

                                {/* Category content: flat list of questions (no subcategory titles in UI) */}
                                <Collapse in={categoryOpen} timeout={300}>
                                    <Box sx={{ pl: 0 }}>
                                        {(
                                            // Category has direct questions (no subCategories)
                                            category.questions?.map((question, questionIndex) => {
                                                const isExpanded = isQuestionExpanded(categoryIndex, null, questionIndex);
                                                const key = `${categoryIndex}-${questionIndex}`;
                                                const isLastQuestion = questionIndex === category.questions!.length - 1;

                                                return (
                                                    <Box key={key} sx={{ width: "100%" }}>
                                                        {/* Question Item */}
                                                        <Box
                                                            onClick={() => handleToggleQuestion(categoryIndex, null, questionIndex)}
                                                            sx={{
                                                                cursor: "pointer",
                                                                py: 1,
                                                                // px: 1,
                                                                width: "100%",
                                                                bgcolor: "#EDEDED",
                                                                display: "flex",
                                                                flexDirection: "row",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body1"
                                                                color={PureLightThemeCaaS.colors.alpha.black[100]}
                                                                sx={{
                                                                    fontSize: "15px",
                                                                    fontWeight: "medium",
                                                                    flex: 1,
                                                                    textAlign: "left",
                                                                }}
                                                            >
                                                                {searchText.trim()
                                                                    ? highlightSearch(question.q, searchText.trim())
                                                                    : question.q}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                sx={{
                                                                    color: PureLightThemeCaaS.colors.alpha.black[100],
                                                                    flexShrink: 0,
                                                                    position: "relative",
                                                                    width: 40,
                                                                    height: 40,
                                                                    "&:hover": { bgcolor: "transparent" },
                                                                    "&:active": { bgcolor: "transparent" },
                                                                    "&.Mui-focusVisible": { bgcolor: "transparent" },
                                                                }}
                                                            >
                                                                {/* {isExpanded ? (
                                                        <RemoveIcon sx={{ fontSize: "20px" }} />
                                                    ) : (
                                                        <AddIcon sx={{ fontSize: "20px" }} />
                                                    )} */}
                                                                <AddIcon
                                                                    sx={{
                                                                        fontSize: "20px",
                                                                        position: "absolute",
                                                                        opacity: isExpanded ? 0 : 1,
                                                                        transform: isExpanded ? "rotate(45deg)" : "rotate(0deg)",
                                                                        transition: "opacity 0.25s ease-in-out, transform 0.3s ease-in-out",
                                                                    }}
                                                                />
                                                                <RemoveIcon
                                                                    sx={{
                                                                        fontSize: "20px",
                                                                        position: "absolute",
                                                                        opacity: isExpanded ? 1 : 0,
                                                                        transition: "opacity 0.25s ease-in-out",
                                                                    }}
                                                                />
                                                            </IconButton>
                                                        </Box>

                                                        {/* Answer (Collapsible) */}
                                                        <Collapse in={isExpanded} timeout={300}>
                                                            <Box
                                                                sx={{
                                                                    px: 1,
                                                                    pb: 2,
                                                                    pt: 0,
                                                                    bgcolor: "#EDEDED",
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    color={PureLightThemeCaaS.colors.alpha.black[100]}
                                                                    sx={{
                                                                        fontSize: "14px",
                                                                        lineHeight: 1.3,
                                                                        whiteSpace: "pre-line",
                                                                    }}
                                                                >
                                                                    {searchText.trim()
                                                                        ? renderAnswerWithHighlight(resolveFaqAnswer(question.a), searchText.trim())
                                                                        : renderAnswer(resolveFaqAnswer(question.a))}
                                                                </Typography>
                                                            </Box>
                                                        </Collapse>

                                                        {/* Divider between questions */}
                                                        {!isLastQuestion && (
                                                            <Divider
                                                                sx={{
                                                                    width: "100%",
                                                                    mx: "auto",
                                                                    height: "1px",
                                                                    bgcolor: `${PureLightThemeCaaS.colors.alpha.black[30]}`,
                                                                    my: 0,
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                );
                                            })
                                        )}
                                    </Box>
                                </Collapse>

                            </Box>
                        );
                    })
                )}
                {/* Footer with download links for each category */}
                {/* <Box sx={{ pt: 0, pb: 1 }}>
                    <Divider
                        sx={{
                            width: "100%",
                            mb: 1,
                            height: "1px",
                            bgcolor: `${PureLightThemeCaaS.colors.alpha.black[30]}`,
                        }}
                    />
                    <Typography
                        variant="body2"
                        color={PureLightThemeCaaS.colors.alpha.black[100]}
                        sx={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            pb: 1.5,
                        }}
                    >
                        Don't let your league-mates out-work you. Get the edge now:
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                        <Link
                            component="button"
                            onClick={(e) => handleStoreClick(e, 'appstore')}
                            sx={{
                                color: 'blue',
                                // textDecoration: 'underline',
                                cursor: 'pointer',
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                font: 'inherit',
                                fontSize: "14px",
                                textAlign: "left",
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            Download RotoGPT on the App Store
                        </Link>
                        <Link
                            component="button"
                            onClick={(e) => handleStoreClick(e, 'playstore')}
                            sx={{
                                color: 'blue',
                                // textDecoration: 'underline',
                                cursor: 'pointer',
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                font: 'inherit',
                                fontSize: "14px",
                                textAlign: "left",
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            Get RotoGPT on Google Play
                        </Link>
                    </Box>
                </Box> */}
            </Box>
        </>
    );
};

export default Faq;
